import Box from '@mui/material/Box'
import Link from '@mui/material/Link'
import { Fragment, type ReactElement } from 'react'
import type { NavigateFunction } from 'react-router-dom'
import {
  REGIONAL_PLAN_NEW_ELEVATION_TOAST_MESSAGES,
  REGIONAL_PLAN_REQUEST_TOAST_MESSAGES,
} from 'src/constants/regionalPlanApprovals.constants'
import {
  buildingTypes,
  classTypes,
  familyTypes,
  REGIONAL_APPROVALS_LABEL,
} from 'src/lib/constants'
import { ROUTES } from 'src/lib/routes'
import type { PropagateFailure } from 'src/types/plan.interface'
import {
  buildApprovedRegionalPlanUrl,
  buildRegionalDraftPlanUrl,
  isLinkableCommunityPlanId,
} from 'src/utils/regionalPlanNavigation.util'

import { LINK_STYLES } from './NotificationMessages.styles'

const NEW_TAB_TARGET = '_blank'

// Shell and unit plans are edited on the multi-family plan page (with their
// building type), while single family plans use the single family plan page.
const buildEditPlanUrl = (planId: string, planClass: classTypes): string => {
  const submitPath = `/edit-plan/${planId}/submit`
  if (planClass === classTypes.SHELL) {
    return `${submitPath}?familyType=${familyTypes.MULTI}&buildingType=${buildingTypes.SHELL}`
  }
  if (planClass === classTypes.UNIT) {
    return `${submitPath}?familyType=${familyTypes.MULTI}&buildingType=${buildingTypes.UNIT}`
  }
  return `${submitPath}?familyType=${familyTypes.SINGLE}`
}

// The admin service names a regional approval `Plan - <number> - <elevation> -
// <community>`. A document without that field still gets a readable label built
// from the fields the request always carries rather than an empty link.
const PLAN_LABEL_PREFIX = 'Plan'
const PLAN_LABEL_SEPARATOR = ' - '
const UNNAMED_PLAN_LABEL = 'a regional plan'

const buildRegionalPlanLabel = (
  planName: string | undefined,
  planNumber: string | undefined,
  draftCommunity: string | undefined
): string => {
  const namedPlan = planName?.trim()
  if (namedPlan) {
    return namedPlan
  }

  const labelParts = [planNumber, draftCommunity]
    .map((part) => part?.trim())
    .filter((part): part is string => Boolean(part))

  return labelParts.length > 0
    ? [PLAN_LABEL_PREFIX, ...labelParts].join(PLAN_LABEL_SEPARATOR)
    : UNNAMED_PLAN_LABEL
}

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

// Collapse any run of whitespace to a single space so a plan name coming back
// from the API matches the same name embedded in the message even when the two
// differ by extra or non-breaking spaces.
const normalizeWhitespace = (value: string): string =>
  value.replace(/\s+/g, ' ').trim()

interface ExportNotificationProps {
  additionalText?: string
  message: string
  navigate: NavigateFunction
}

const ExportNotification: React.FC<ExportNotificationProps> = ({
  additionalText,
  message,
  navigate,
}) => (
  <Box>
    {message} Click{' '}
    <Link
      underline="always"
      onClick={(event) => {
        event.preventDefault()
        if (event.metaKey || event.ctrlKey) {
          window.open(ROUTES.EXPORTS, NEW_TAB_TARGET)
        } else {
          navigate(ROUTES.EXPORTS)
        }
      }}
      sx={LINK_STYLES}
    >
      here
    </Link>{' '}
    or check the Exports link in the left-hand navigation panel
    {additionalText ? ` ${additionalText}.` : '.'}
  </Box>
)

export const createApprovalReportExportStartedNotification = (
  navigate: NavigateFunction
): ReactElement => (
  <ExportNotification
    additionalText="for more info"
    message="Export has been started."
    navigate={navigate}
  />
)

export const createApprovalReportExportSuccessNotification = (
  navigate: NavigateFunction
): ReactElement => (
  <ExportNotification
    message="Export has been completed successfully."
    navigate={navigate}
  />
)

export const createApprovalReportExportFailureNotification =
  (): ReactElement => (
    <Box>Failed to complete the export. Please try again later.</Box>
  )

interface PropagateFailureNotificationProps {
  failures: PropagateFailure[]
  message: string
  planClass: classTypes
}

const PropagateFailureNotification: React.FC<
  PropagateFailureNotificationProps
> = ({ failures, message, planClass }) => {
  const planIdsByName = new Map(
    failures.map((failure) => [
      normalizeWhitespace(failure.planName),
      failure.planId,
    ])
  )
  const pattern = failures
    .map((failure) => escapeRegExp(failure.planName).replace(/\s+/g, '\\s+'))
    .join('|')
  const segments = pattern
    ? message.split(new RegExp(`(${pattern})`, 'g'))
    : [message]

  return (
    <Box>
      {segments.map((segment, index) => {
        const planId = planIdsByName.get(normalizeWhitespace(segment))
        if (!planId) {
          return <Fragment key={`text-${index}`}>{segment}</Fragment>
        }
        return (
          <Link
            key={`plan-${planId}-${index}`}
            underline="always"
            onClick={(event) => {
              event.preventDefault()
              // Stop the click from bubbling to the toast, which would otherwise
              // dismiss it and prevent clicking the remaining plan links.
              event.stopPropagation()
              window.open(buildEditPlanUrl(planId, planClass), NEW_TAB_TARGET)
            }}
            sx={LINK_STYLES}
          >
            {segment}
          </Link>
        )
      })}
    </Box>
  )
}

export const createPropagateFailureNotification = (
  message: string,
  failures: PropagateFailure[],
  planClass: classTypes
): ReactElement => (
  <PropagateFailureNotification
    failures={failures}
    message={message}
    planClass={planClass}
  />
)

interface RegionalApprovalDecisionNotificationProps {
  draftPlanId: string
  message: string
  planName: string
}

/**
 * LTGC-5711: announces an approver's decision on a regional plan copy, linking
 * straight to the draft it decided.
 */
const RegionalApprovalDecisionNotification: React.FC<
  RegionalApprovalDecisionNotificationProps
> = ({ draftPlanId, message, planName }) => {
  // The id arrives on a SignalR broadcast, so it is external input reaching a
  // URL sink. Only a well-formed community plan id becomes a link; anything
  // else still announces the decision, just without one to click.
  if (!isLinkableCommunityPlanId(draftPlanId)) {
    return <Box>{message}</Box>
  }

  return (
    <Box>
      {message}{' '}
      <Link
        underline="always"
        onClick={(event) => {
          event.preventDefault()
          // Stop the click from bubbling to the toast, which would dismiss it
          // before the new tab opens.
          event.stopPropagation()
          window.open(buildRegionalDraftPlanUrl(draftPlanId), NEW_TAB_TARGET)
        }}
        sx={LINK_STYLES}
      >
        {planName}
      </Link>
    </Box>
  )
}

export const createRegionalApprovalDecisionNotification = (
  message: string,
  planName: string,
  draftPlanId: string
): ReactElement => (
  <RegionalApprovalDecisionNotification
    draftPlanId={draftPlanId}
    message={message}
    planName={planName}
  />
)

/** The fields of a regional plan approval a request toast needs to name it. */
export interface RegionalApprovalRequestDetails {
  draftCommunity: string
  draftPlanId: string
  planName: string
  planNumber: string
}

interface RegionalApprovalRequestNotificationProps
  extends RegionalApprovalRequestDetails {
  navigate: NavigateFunction
}

/** "Review it on the Regional Plan Approvals tab.", with the tab as a link. */
const ReviewOnQueuePrompt: React.FC<{ navigate: NavigateFunction }> = ({
  navigate,
}) => (
  <Fragment>
    {REGIONAL_PLAN_REQUEST_TOAST_MESSAGES.REVIEW_PROMPT}{' '}
    <Link
      underline="always"
      onClick={(event) => {
        event.preventDefault()
        // The toast must outlive the click, or a ctrl/cmd-click dismisses it
        // before the new tab opens.
        event.stopPropagation()
        if (event.metaKey || event.ctrlKey) {
          window.open(ROUTES.REGIONAL_APPROVALS, NEW_TAB_TARGET)
        } else {
          navigate(ROUTES.REGIONAL_APPROVALS)
        }
      }}
      sx={LINK_STYLES}
    >
      {REGIONAL_APPROVALS_LABEL}
    </Link>{' '}
    {REGIONAL_PLAN_REQUEST_TOAST_MESSAGES.REVIEW_PROMPT_SUFFIX}
  </Fragment>
)

/**
 * LTGC-5711: announces a newly arrived regional plan copy request to the
 * approver whose region owns it, linking both to the draft awaiting a decision
 * and to the queue it is waiting in.
 */
const RegionalApprovalRequestNotification: React.FC<
  RegionalApprovalRequestNotificationProps
> = ({ draftCommunity, draftPlanId, navigate, planName, planNumber }) => {
  const planLabel = buildRegionalPlanLabel(planName, planNumber, draftCommunity)
  // The id arrives on a SignalR broadcast, so it is external input reaching a
  // URL sink. Only a well-formed community plan id becomes a link; anything
  // else still names the plan, just without one to click.
  const isDraftLinkable = isLinkableCommunityPlanId(draftPlanId)

  return (
    <Box>
      {REGIONAL_PLAN_REQUEST_TOAST_MESSAGES.LEAD_IN}{' '}
      {isDraftLinkable ? (
        <Link
          underline="always"
          onClick={(event) => {
            event.preventDefault()
            // Stop the click from bubbling to the toast, which would dismiss it
            // before the new tab opens.
            event.stopPropagation()
            window.open(buildRegionalDraftPlanUrl(draftPlanId), NEW_TAB_TARGET)
          }}
          sx={LINK_STYLES}
        >
          {planLabel}
        </Link>
      ) : (
        <Box component="span">{planLabel}</Box>
      )}
      {'. '}
      <ReviewOnQueuePrompt navigate={navigate} />
    </Box>
  )
}

export const createRegionalApprovalRequestNotification = (
  approval: RegionalApprovalRequestDetails,
  navigate: NavigateFunction
): ReactElement => (
  <RegionalApprovalRequestNotification
    draftCommunity={approval.draftCommunity}
    draftPlanId={approval.draftPlanId}
    navigate={navigate}
    planName={approval.planName}
    planNumber={approval.planNumber}
  />
)

interface ApprovedRegionalPlanLinkProps {
  planName: string
  sourcePlanId: string
}

/**
 * Names an Approved Regional Plan, opening it in a new tab when its id is safe
 * to interpolate. The id arrives on a SignalR broadcast, so it is external
 * input reaching a URL sink: a malformed one still names the plan, unlinked.
 */
const ApprovedRegionalPlanLink: React.FC<ApprovedRegionalPlanLinkProps> = ({
  planName,
  sourcePlanId,
}) => {
  if (!isLinkableCommunityPlanId(sourcePlanId)) {
    return <Box component="span">{planName}</Box>
  }
  return (
    <Link
      underline="always"
      onClick={(event) => {
        event.preventDefault()
        // Stop the click from bubbling to the toast, which would dismiss it
        // before the new tab opens.
        event.stopPropagation()
        window.open(buildApprovedRegionalPlanUrl(sourcePlanId), NEW_TAB_TARGET)
      }}
      sx={LINK_STYLES}
    >
      {planName}
    </Link>
  )
}

/** The fields of a new-elevation request a toast needs to name it. */
export interface RegionalNewElevationRequestDetails {
  planName: string
  sourcePlanId: string
  userDivision: string
}

type RegionalPlanDetails = Omit<
  RegionalNewElevationRequestDetails,
  'userDivision'
>

interface RegionalElevationAddedNotificationProps extends RegionalPlanDetails {
  /** The elevations that have just appeared on the plan, upper-cased. */
  addedElevations: string[]
}

/**
 * Tells the requester that the Approved Regional Plan they asked about has
 * gained elevations, linking to the plan so they can copy one.
 */
const RegionalElevationAddedNotification: React.FC<
  RegionalElevationAddedNotificationProps
> = ({ addedElevations, planName, sourcePlanId }) => (
  <Box>
    {REGIONAL_PLAN_NEW_ELEVATION_TOAST_MESSAGES.ELEVATION_ADDED(
      addedElevations
    )}{' '}
    <ApprovedRegionalPlanLink planName={planName} sourcePlanId={sourcePlanId} />{' '}
    {REGIONAL_PLAN_NEW_ELEVATION_TOAST_MESSAGES.ELEVATION_ADDED_SUFFIX}
  </Box>
)

export const createRegionalElevationAddedNotification = (
  request: RegionalPlanDetails,
  addedElevations: string[]
): ReactElement => (
  <RegionalElevationAddedNotification
    addedElevations={addedElevations}
    planName={request.planName}
    sourcePlanId={request.sourcePlanId}
  />
)

/**
 * Tells the requester that the regional lead has marked their new-elevation
 * request complete, linking to the plan so they can copy the elevation.
 */
const RegionalNewElevationCompletedNotification: React.FC<
  RegionalPlanDetails
> = ({ planName, sourcePlanId }) => (
  <Box>
    {REGIONAL_PLAN_NEW_ELEVATION_TOAST_MESSAGES.REQUEST_COMPLETED}{' '}
    <ApprovedRegionalPlanLink planName={planName} sourcePlanId={sourcePlanId} />{' '}
    {REGIONAL_PLAN_NEW_ELEVATION_TOAST_MESSAGES.REQUEST_COMPLETED_SUFFIX}
  </Box>
)

export const createRegionalNewElevationCompletedNotification = (
  request: RegionalPlanDetails
): ReactElement => (
  <RegionalNewElevationCompletedNotification
    planName={request.planName}
    sourcePlanId={request.sourcePlanId}
  />
)

interface RegionalNewElevationRequestNotificationProps
  extends RegionalNewElevationRequestDetails {
  navigate: NavigateFunction
}

/**
 * Announces a newly filed new-elevation request to the lead whose region owns
 * it, linking to the Approved Regional Plan it concerns and to the queue.
 */
const RegionalNewElevationRequestNotification: React.FC<
  RegionalNewElevationRequestNotificationProps
> = ({ navigate, planName, sourcePlanId, userDivision }) => (
  <Box>
    {REGIONAL_PLAN_NEW_ELEVATION_TOAST_MESSAGES.REQUEST_LEAD_IN(userDivision)}{' '}
    <ApprovedRegionalPlanLink planName={planName} sourcePlanId={sourcePlanId} />
    {'. '}
    <ReviewOnQueuePrompt navigate={navigate} />
  </Box>
)

export const createRegionalNewElevationRequestNotification = (
  request: RegionalNewElevationRequestDetails,
  navigate: NavigateFunction
): ReactElement => (
  <RegionalNewElevationRequestNotification
    navigate={navigate}
    planName={request.planName}
    sourcePlanId={request.sourcePlanId}
    userDivision={request.userDivision}
  />
)
